import React from 'react';
import classNames from 'classnames';
import { capitalize } from 'inflection';

import Button, {
  BUTTON_SIZES,
  BUTTON_WIDTHS
} from '@buyside/components/presentation/Button/button';
import CloseQuickPositionModal from '@buyside/components/presentation/QuickPosition/CloseQuickPositionModal/closeQuickPositionModal';
import OpenQuickPositionForm from '@buyside/components/presentation/QuickPosition/OpenQuickPositionForm/openQuickPositionForm';
import Skeleton, {
  SKELETON_SIZES,
  SKELETON_WIDTHS
} from '@buyside/components/presentation/Skeleton/skeleton';

import { quickPositionsPageUrl } from '@buyside/util/urlHelper';

import UpArrowIcon from '@buyside/svgs/up_arrow.svg';
import DownArrowIcon from '@buyside/svgs/down_arrow.svg';

import { useQuickPositionAssetQuery } from '@interfaces/graphql-typings';

import styles from './quickPositionStyles.scss';

const QuickPosition: React.FC<{ assetId: string }> = ({ assetId }) => {
  const { data, loading } = useQuickPositionAssetQuery({ variables: { assetId } });

  if (loading || !data?.asset)
    return (
      <div className={styles.quickPositionContainer} data-testid="quick-position-skeleton">
        <Skeleton width={SKELETON_WIDTHS.FULL} size={SKELETON_SIZES.SMALL} />
        <Skeleton width={SKELETON_WIDTHS.FULL} size={SKELETON_SIZES.SMALL} />
        <Skeleton width={SKELETON_WIDTHS.ONE_THIRD} size={SKELETON_SIZES.SMALL} />
      </div>
    );

  const {
    currencyCode,
    currentUserQuickPosition: quickPosition,
    latestPrice,
    longQuickPositionRatio,
    shortQuickPositionRatio,
    symbol
  } = data.asset;

  const isCurrentUserHidden = Boolean(data.currentUser?.isHidden);

  if (!quickPosition)
    return (
      <div className={styles.quickPositionContainer}>
        <OpenQuickPositionForm
          assetId={assetId}
          currencyCode={currencyCode}
          isCurrentUserHidden={isCurrentUserHidden}
          latestPrice={latestPrice}
          symbol={symbol}
        />
      </div>
    );

  const {
    id: quickPositionId,
    chat,
    direction,
    openedAt,
    openingPrice,
    returnToDate
  } = quickPosition;

  return (
    <div className={styles.quickPositionContainer}>
      <div className={styles.quickPosition}>
        <div className={styles.assetInfo}>
          <p>{symbol}</p>
          <p className={classNames(styles.direction, { [styles.long]: direction === 'long' })}>
            {direction === 'long' ? <UpArrowIcon /> : <DownArrowIcon />}
            {capitalize(direction)}
          </p>
        </div>
        <div className={styles.datapoint}>
          <label>Gain/Loss</label>
          <p>{returnToDate}%</p>
        </div>
        <div className={styles.datapoint}>
          <label>Opened On</label>
          <p>
            {openedAt} at {openingPrice} {currencyCode}
          </p>
        </div>
        <div>
          <CloseQuickPositionModal
            assetId={assetId}
            currencyCode={currencyCode}
            latestPrice={latestPrice}
            quickPositionId={quickPositionId}
            symbol={symbol}
          />
          <Button
            size={BUTTON_SIZES.TINY}
            text="View All My Positions"
            url={quickPositionsPageUrl()}
            width={BUTTON_WIDTHS.FULL}
          />
        </div>
      </div>
      <div className={styles.communityConsensus}>
        <strong>Community Consensus</strong>
        <div>
          <label>Long</label>
          <div className={styles.progressBarContainer}>
            <div className={styles.progressBarValue}>{longQuickPositionRatio}%</div>
            <div
              className={styles.progressBar}
              style={{ width: `${longQuickPositionRatio}%` }}
              data-testid="long-ratio-bar"
            />
          </div>
        </div>
        <div>
          <label>Short</label>
          <div className={styles.progressBarContainer}>
            <div className={styles.progressBarValue}>{shortQuickPositionRatio}%</div>
            <div
              className={styles.shortProgressBar}
              style={{ width: `${shortQuickPositionRatio}%` }}
              data-testid="short-ratio-bar"
            />
          </div>
        </div>
        <Button
          size={BUTTON_SIZES.TINY}
          text={`${symbol} Community Chat`}
          url={quickPositionsPageUrl(chat.id)}
          width={BUTTON_WIDTHS.FULL}
        />
      </div>
    </div>
  );
};

export default QuickPosition;
